import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useI18n } from "../../i18n/I18nProvider";
import { readRecentWorkspaces, rememberWorkspace } from "../../lib/recent-workspaces";
import { orderMemberships } from "../../features/workspace/order-memberships";
import { useWorkspaceMemberships, workspaceQuery } from "../../features/workspace/queries";

export interface WorkspaceSwitcherProps {
  currentId?: string | undefined;
}

/**
 * Compact switcher between the signed-in operator's workspaces (the server's memberships), with
 * this browser's recent ones first.
 */
export interface WorkspaceOption {
  id: string;
  name: string;
}

/**
 * The operator's live memberships, recent-first. The current workspace is added when it is not a
 * membership but still opens (an admin acting as it), so the select always shows a real option.
 */
export function useWorkspaceOptions(currentId?: string): WorkspaceOption[] {
  const memberships = useWorkspaceMemberships();
  const current = useQuery({
    ...workspaceQuery(currentId ?? ""),
    enabled: Boolean(currentId),
    retry: false,
  });
  const ordered = orderMemberships(memberships.data ?? [], readRecentWorkspaces()).map(
    ({ id, name }) => ({ id, name }),
  );
  if (currentId && current.data && !ordered.some((option) => option.id === currentId)) {
    return [{ id: currentId, name: current.data.name }, ...ordered];
  }
  return ordered;
}

export function WorkspaceSwitcher({ currentId }: WorkspaceSwitcherProps) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const options = useWorkspaceOptions(currentId);

  if (options.length === 0) return null;

  return (
    <label className="flex items-center gap-2 text-note text-ink-2">
      <span className="sr-only lg:not-sr-only">{t("workspace.switch")}</span>
      <select
        aria-label={t("workspace.switch")}
        value={currentId ?? ""}
        onChange={(event) => {
          const next = event.target.value;
          if (!next) return;
          rememberWorkspace(next);
          void navigate({ to: "/app/$workspaceId/roster", params: { workspaceId: next } });
        }}
        className="select-field h-11 max-w-[12rem] rounded-control border border-edge bg-board py-0 pr-9 pl-3 text-operate text-ink"
      >
        {!currentId ? <option value="">{t("workspace.yours")}</option> : null}
        {options.map((option) => (
          <option key={option.id} value={option.id}>
            {option.name}
          </option>
        ))}
      </select>
    </label>
  );
}
